import { Link } from 'react-router-dom';

type SupportedPlatform = { name: string; comingSoon?: boolean };

const PLATFORM_CATEGORIES: Array<{
  title: string;
  platforms: SupportedPlatform[];
}> = [
  {
    title: 'Advertising',
    platforms: [
      { name: 'Google Ads' },
      { name: 'Display & Video 360 (DV360)' },
      { name: 'Campaign Manager 360 (CM360)' },
      { name: 'X (formerly Twitter)' },
      { name: 'LinkedIn' },
      { name: 'TikTok' },
      { name: 'Snapchat' },
      { name: 'Facebook', comingSoon: true },
      { name: 'Instagram', comingSoon: true },
    ],
  },
  {
    title: 'KPI',
    platforms: [{ name: 'Google Analytics 4' }, { name: 'Shopify' }],
  },
];

export function IntegrationsPage() {
  return (
    <>
      <header className="nk-header" id="home">
        <div className="section section-lg section-b-0">
          <div className="section-heading">
            <div className="container">
              <div className="row justify-content-between align-items-center">
                <div className="col-lg-6 col-xl-5">
                  <div className="text-block">
                    <div className="fs-9 text-uppercase text-gradient-a w-max mb-4">
                      Integrations
                    </div>
                    <h2 className="title text-gradient-heading fw-medium fs-1 mb-4">
                      Connect your advertising and KPI platforms
                    </h2>
                    <p className="fs-8">
                      Yapamar reads daily campaign performance from the
                      advertising and KPI platforms you already use, turning
                      what they expose through their official APIs into a single
                      view of what&rsquo;s actually moving your sales.
                    </p>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="gfx me-xl-6 mt-xl-n4">
                    <img
                      src="/marketing/landing/home-how-it-works-1.png"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="nk-main">
        {/* Supported Platforms */}
        <div className="section section-lg section-t-0">
          <div className="section-heading">
            <div className="container">
              <div className="row justify-content-center text-center">
                <div className="col-xl-7 col-lg-9">
                  <h3 className="title text-title fs-2 mb-3">
                    Supported platforms
                  </h3>
                  <p className="fs-8">
                    Each integration is an official API connection, established
                    by you once and refreshed automatically while access is
                    granted.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="section-content">
            <div className="container">
              {PLATFORM_CATEGORIES.map(({ title, platforms }, index) => (
                <div key={title} className={index > 0 ? 'mt-6' : undefined}>
                  <h4 className="fs-5 text-center mb-4">{title}</h4>
                  <div className="row g-gs justify-content-center">
                    {platforms.map(({ name, comingSoon }) => (
                      <div key={name} className="col-12 col-sm-6 col-md-4">
                        <div className="card card-bordered bg-background h-100 text-center">
                          <div className="card-body py-5">
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <h5 className="fs-7 mb-0">{name}</h5>
                              {comingSoon && (
                                <span className="badge bg-primary bg-opacity-20 text-primary fs-10">
                                  Coming soon
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="gradient-separator"></div>

        {/* How Integrations Work */}
        <div className="section section-lg">
          <div className="section-heading">
            <div className="container">
              <div className="row justify-content-center text-center">
                <div className="col-xl-7 col-lg-9">
                  <h3 className="title text-title fs-2 mb-3">
                    How integrations work
                  </h3>
                  <p className="fs-8">
                    Connections use the official APIs published by each
                    advertising and KPI platform — no scraping, no credential
                    entry into Yapamar.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="section-content">
            <div className="container">
              <div className="row g-gs justify-content-center">
                <div className="col-md-6 col-xl-4">
                  <div className="card card-bordered bg-background h-100">
                    <div className="card-body">
                      <h5 className="fs-7 mb-2">Secure OAuth 2.0</h5>
                      <p className="fs-8 mb-0">
                        You authorize Yapamar in the platform&rsquo;s own login
                        screen (Google, Meta, LinkedIn, etc.). We receive a
                        scoped access token; we never see or store your username
                        or password.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4">
                  <div className="card card-bordered bg-background h-100">
                    <div className="card-body">
                      <h5 className="fs-7 mb-2">Read-only access</h5>
                      <p className="fs-8 mb-0">
                        We request the minimum scopes required to read campaign
                        performance. Yapamar does not have write access to your
                        accounts and cannot change configuration on your behalf.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4">
                  <div className="card card-bordered bg-background h-100">
                    <div className="card-body">
                      <h5 className="fs-7 mb-2">
                        No campaign editing or bidding
                      </h5>
                      <p className="fs-8 mb-0">
                        Yapamar never edits campaigns, changes budgets, adjusts
                        bids, or pauses ads. Recommendations are shown inside
                        Yapamar; you apply them yourself in the source platform.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="gradient-separator"></div>

        {/* Data We Access */}
        <div className="section section-lg">
          <div className="section-heading">
            <div className="container">
              <div className="row justify-content-center text-center">
                <div className="col-xl-7 col-lg-9">
                  <h3 className="title text-title fs-2 mb-3">Data we access</h3>
                  <p className="fs-8">
                    Yapamar only retrieves the data needed to build a Marketing
                    Mix Model and generate budget recommendations. Nothing is
                    used for advertising, profiling, or resale.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="section-content">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-xl-8">
                  <div className="card card-bordered bg-background">
                    <div className="card-body">
                      <p className="fs-8 mb-3">
                        From every connected advertising and KPI platform,
                        Yapamar reads the same compact set of data:
                      </p>
                      <ul className="fs-8 mb-4">
                        <li>
                          The list of <strong>ad accounts</strong> you have
                          authorized access to.
                        </li>
                        <li>
                          <strong>Campaign structure and metadata</strong> —
                          campaign IDs, names, and (where the platform exposes
                          it) a sub-breakdown such as ad network on Google Ads
                          or publisher platform on Meta.
                        </li>
                        <li>
                          <strong>Daily performance metrics</strong> at the
                          campaign level: impressions, clicks, cost or spend,
                          and conversions.
                        </li>
                      </ul>
                      <p className="fs-8 mb-3">
                        Yapamar does <strong>not</strong> retrieve:
                      </p>
                      <ul className="fs-8 mb-4">
                        <li>Ad-group and ad-creative detail.</li>
                        <li>
                          Personally identifiable information about end users.
                        </li>
                        <li>
                          Lists of customers, leads, or email/phone audiences.
                        </li>
                        <li>
                          Account billing details, payment instruments, or
                          credentials.
                        </li>
                      </ul>
                      <p className="fs-8 mb-0">
                        <strong>Purpose of access:</strong> this data is used
                        exclusively to (a) decompose marketing effectiveness by
                        channel, (b) train Marketing Mix Models, and (c) produce
                        budget allocation recommendations shown back to you
                        inside Yapamar. It is not sold, shared with third
                        parties for their own purposes, or used for advertising
                        targeting.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="gradient-separator"></div>

        {/* User Control & Privacy */}
        <div className="section section-lg">
          <div className="section-heading">
            <div className="container">
              <div className="row justify-content-center text-center">
                <div className="col-xl-7 col-lg-9">
                  <h3 className="title text-title fs-2 mb-3">
                    User control &amp; privacy
                  </h3>
                  <p className="fs-8">
                    You stay in control of every connection, both inside Yapamar
                    and in the source platform.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="section-content">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-xl-8">
                  <div className="card card-bordered bg-background">
                    <div className="card-body">
                      <ul className="fs-8 mb-3">
                        <li>
                          Each integration can be{' '}
                          <strong>
                            connected and disconnected at any time
                          </strong>{' '}
                          from inside Yapamar. Disconnecting stops further data
                          collection immediately.
                        </li>
                        <li>
                          You can also{' '}
                          <strong>
                            revoke Yapamar&rsquo;s access directly in the source
                            platform
                          </strong>{' '}
                          — for example, by removing Yapamar from your{' '}
                          <a
                            href="https://myaccount.google.com/permissions"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'underline' }}>
                            Google Account permissions
                          </a>
                          . The access token becomes unusable from that moment.
                        </li>
                        <li>
                          We retain previously-collected performance data only
                          as needed to keep your historical models available;
                          deletion of an account in Yapamar removes the
                          associated data in line with our retention policy.
                        </li>
                      </ul>
                      <p className="fs-8 mb-0">
                        Detailed handling of your data is described in our{' '}
                        <Link
                          to="/privacy-policy"
                          style={{ textDecoration: 'underline' }}>
                          Privacy Policy
                        </Link>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="gradient-separator"></div>

        {/* Security */}
        <div className="section section-lg">
          <div className="section-heading">
            <div className="container">
              <div className="row justify-content-center text-center">
                <div className="col-xl-7 col-lg-9">
                  <h3 className="title text-title fs-2 mb-3">Security</h3>
                </div>
              </div>
            </div>
          </div>
          <div className="section-content">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-xl-8">
                  <div className="card card-bordered bg-background">
                    <div className="card-body">
                      <p className="fs-8 mb-0">
                        Yapamar uses each platform&rsquo;s official API and the
                        OAuth 2.0 flow it publishes. We do not store usernames,
                        passwords, or any other login credentials — only the
                        short-lived access and refresh tokens issued by the
                        platform after you authorize access. Tokens are
                        encrypted at rest and used exclusively to pull
                        performance data for your Marketing Mix Models.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
